import { useCallback, useEffect, useRef } from "react";
import {
  type AuthCallbackParams,
  parseAuthParamsFromUrl,
} from "../lib/auth-callback";
import { buildHistoryPathWithoutAuthParams } from "../lib/forum-invite-deeplink";
import { getRuntimeApiBase } from "../lib/runtime-api-base";

const API_BASE = getRuntimeApiBase();

interface UseDesktopAuthCallbacksInput {
  enabled: boolean;
  handleAuthData: (params: AuthCallbackParams) => Promise<boolean>;
}

export function useDesktopAuthCallbacks({
  enabled,
  handleAuthData,
}: UseDesktopAuthCallbacksInput) {
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleDeepLink = async (url: string) => {
      const params = parseAuthParamsFromUrl(url);
      await handleAuthData(params);
    };

    window.electron?.onAuthCallback(handleDeepLink);
    return () => window.electron?.onAuthCallback(null);
  }, [enabled, handleAuthData]);

  useEffect(() => {
    if (!enabled) return;

    const params = parseAuthParamsFromUrl(window.location.href);
    if (params.access_token || params.code) {
      void (async () => {
        const consumed = await handleAuthData(params);
        if (consumed && (window.location.hash || window.location.search)) {
          window.history.replaceState(
            {},
            "",
            buildHistoryPathWithoutAuthParams(window.location.href),
          );
        }
      })();
    }
  }, [enabled, handleAuthData]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/supabase/pending`);
        if (!res.ok) return;
        const data = (await res.json()) as AuthCallbackParams;
        if (data.access_token || data.code) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          await handleAuthData(data);
        }
      } catch {
        // Polling is a fallback; deep link delivery remains primary.
      }
    }, 1500);
    setTimeout(() => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }, 120_000);
  }, [handleAuthData]);

  useEffect(
    () => () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    },
    [],
  );

  return startPolling;
}
